<script setup lang="ts">
import { computed, nextTick, provide, ref, watch } from 'vue';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { type ModalV2, type ModalV2Props, useModalStore } from './modal-store';
import IconButton from '../IconButton/IconButton.vue';
import { MODAL_BASE_Z_INDEX, modalLayerKey } from '../layering/layering';
import {
  type ModalCloseGuard,
  setModalCloseGuardKey,
} from './modal-close-guard';

const props = defineProps<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modal: ModalV2<ModalV2Props<any>>;
}>();

const modalStore = useModalStore();
const closeGuard = ref<ModalCloseGuard | null>(null);
const dialogPanelRef = ref<HTMLElement | null>(null);

provide(setModalCloseGuardKey, (guard: ModalCloseGuard | null) => {
  closeGuard.value = guard;
});

const isTopModal = computed(() => {
  const topModal = modalStore.modals.value[modalStore.modals.value.length - 1];
  return topModal.id === props.modal.props.id;
});

const DIALOG_ROLE_SELECTOR = '[role="dialog"], [role="alertdialog"]';

const resolvePanelElement = (): HTMLElement | null => {
  const panel = dialogPanelRef.value as
    HTMLElement | { $el?: HTMLElement } | null;

  if (panel instanceof HTMLElement) {
    return panel;
  }

  return panel?.$el ?? null;
};

// A foreign overlay — e.g. a legacy `Modal`/`TDialog` rendered in its own
// teleport that Headless UI's dialog stack cannot see — can sit above this
// modal. Headless UI still fires `@close` on clicks/Escape that belong to that
// overlay, which would wrongly dismiss this modal from underneath it. Such an
// overlay mounts later in the document, so it is the last dialog in DOM order;
// only honor the close when *this* dialog is the top-most one on screen.
const isTopMostDialog = (): boolean => {
  const root = resolvePanelElement()?.closest(DIALOG_ROLE_SELECTOR) ?? null;
  if (root === null) {
    return true;
  }

  const dialogs = document.querySelectorAll(DIALOG_ROLE_SELECTOR);
  return dialogs[dialogs.length - 1] === root;
};

async function closeModal() {
  if (!isTopModal.value || !isTopMostDialog()) {
    return;
  }

  if (closeGuard.value) {
    const shouldClose = await closeGuard.value();
    if (!shouldClose) {
      return;
    }
  }

  props.modal.props.resolve({
    value: undefined,
    cancelled: true,
  });
}

const isOpen = computed(() => {
  const modalObject = modalStore.modals.value.find(
    (m) => m.id === props.modal.props.id,
  );
  return modalObject?.isOpen;
});

const isDialogOpen = computed(() => isOpen.value && isTopModal.value);

const stackIndex = computed(() => {
  const index = modalStore.modals.value.findIndex(
    (m) => m.id === props.modal.props.id,
  );
  return index === -1 ? 0 : index;
});

const zIndex = computed(() => {
  // A higher base on an earlier modal must also lift its stacked dialogs.
  const base = modalStore.modals.value
    .slice(0, stackIndex.value + 1)
    .reduce(
      (highestBase, modal) =>
        Math.max(highestBase, modal.zIndexBase ?? MODAL_BASE_Z_INDEX),
      MODAL_BASE_Z_INDEX,
    );
  return base + stackIndex.value;
});
provide(modalLayerKey, zIndex);

// Keep non-top and closing modals in the DOM for stacked/leave transitions,
// while only the top open modal traps focus.
const isDialogStatic = computed(() => !isTopModal.value || !isOpen.value);

const isBackgroundModal = computed(() => !isTopModal.value && isOpen.value);

const viewProps = computed<Record<string, unknown>>(
  () => (props.modal.props as Record<string, unknown> | undefined) ?? {},
);

// Prefer an explicit autofocus target, then the first editable text field.
// Hidden, disabled and inert fields must not capture focus.
const initialFocus = computed((): HTMLElement | null => {
  const panel = resolvePanelElement();
  if (!panel) return null;
  const candidates = panel.querySelectorAll<HTMLElement>(
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"]',
  );
  const autofocus = panel.querySelectorAll<HTMLElement>('[autofocus]');
  return (
    [...autofocus, ...candidates].find(
      (element) =>
        !element.matches(':disabled, [readonly], [tabindex="-1"]') &&
        !element.closest('[inert]') &&
        element.getClientRects().length > 0,
    ) ?? panel
  );
});

watch(
  [isDialogOpen, dialogPanelRef],
  ([open]) => {
    if (!open) return;
    void nextTick(() => {
      const panel = resolvePanelElement();
      if (!panel) return;
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active !== panel &&
        panel.contains(active)
      )
        return;
      initialFocus.value?.focus();
    });
  },
  { flush: 'post' },
);
</script>

<template>
  <Teleport to="body">
    <TransitionRoot appear :show="isOpen">
      <Dialog
        class="relative"
        :class="{ 'pointer-events-none': isBackgroundModal }"
        :style="{ zIndex }"
        :open="isDialogOpen"
        :static="isDialogStatic"
        :initial-focus="initialFocus"
        @close="closeModal"
      >
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/25" />
        </TransitionChild>
        <div class="fixed inset-0">
          <div class="flex h-full items-center justify-center text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel
                ref="dialogPanelRef"
                tabindex="-1"
                :inert="isBackgroundModal"
                class="flex max-h-full w-full min-w-0 transform flex-col rounded-2xl bg-white text-left align-middle shadow-xl transition-all outline-none"
                data-testid="modal-v2-container"
                :class="[
                  props.modal.size === 'sm' && 'max-w-sm px-5 pt-6 pb-5',
                  props.modal.size === 'md' && 'max-w-lg px-5 pt-6 pb-5',
                  props.modal.size === 'lg' && 'max-w-3xl px-5 pt-6 pb-5',
                  props.modal.size === 'wide' && 'max-w-4xl px-5 pt-6 pb-5',
                  props.modal.size === 'xl' &&
                    'h-full max-w-full rounded-none px-5 pt-6 pb-5 sm:h-[90vh] sm:max-w-6xl sm:rounded-2xl',
                  props.modal.size === 'full' &&
                    'h-full max-w-full px-5 pt-6 pb-5',
                  props.modal.size === 'entire-screen' &&
                    'h-full max-w-full rounded-none p-0',
                ]"
              >
                <div class="flex min-h-0 min-w-0 flex-1 flex-col">
                  <div
                    v-if="
                      props.modal.title && props.modal.size !== 'entire-screen'
                    "
                    class="mb-4 flex items-center"
                  >
                    <h3
                      class="flex-1 font-bold"
                      :class="[
                        props.modal.titleSize === 'sm' && 'text-lg',
                        props.modal.titleSize === 'md' && 'text-xl',
                        (!props.modal.titleSize ||
                          props.modal.titleSize === 'lg') &&
                          'text-2xl',
                      ]"
                    >
                      {{ props.modal.title }}
                    </h3>
                    <IconButton
                      variant="ghost-neutral"
                      icon="cancel"
                      tabindex="-1"
                      data-testid="modal-close-button"
                      @click="closeModal"
                    />
                  </div>
                  <div class="flex min-h-0 min-w-0 flex-1">
                    <component :is="props.modal.component" v-bind="viewProps" />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>
